import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { clientApi, setClientToken, getClientToken, errMsg } from "@/lib/api";
import { applyBrandColor } from "@/lib/theme";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { brl, toISODateInput, fmtDate, fmtDateTime, fmtTime } from "@/lib/format";
import { Clock, Route, ChevronLeft, ChevronRight, Loader2, CheckCircle2, Phone, Mail, Instagram, Globe, MessageCircle, LogOut, CalendarCheck, CalendarClock, XCircle, Pencil, Scissors } from "lucide-react";
import InstallBanner from "@/components/InstallBanner";
import { applyTenantManifest } from "@/lib/pwa";
import { formatAppointmentServices } from "@/lib/appointment";
import CepField from "@/components/CepField";
import { AvailabilityCalendar } from "@/components/AvailabilityCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAY_PT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

function BrandHeader({ brand }) {
  return (
    <>
      <div className="relative h-48 md:h-64 overflow-hidden">
        {brand.cover_url ? (
          <img src={brand.cover_url} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/80 to-primary/60" />
        )}
        <div className="absolute inset-0 bg-primary/60" />
        <div className="relative h-full flex items-end p-6 md:p-10 text-primary-foreground">
          <div className="flex items-center gap-4">
            {brand.logo_url && (
              <img src={brand.logo_url} alt="Logo" className="h-16 w-16 md:h-20 md:w-20 rounded-xl object-contain bg-white/95 p-2 border-2 border-white/30"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            )}
            <div>
              <div className="text-xs uppercase tracking-widest opacity-80">Agende online</div>
              <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight">{brand.business_name || "Reserve seu horário"}</h1>
              {brand.tagline && <p className="text-sm md:text-base opacity-90 mt-1">{brand.tagline}</p>}
            </div>
          </div>
        </div>
      </div>
      {(brand.phone || brand.whatsapp || brand.email || brand.instagram || brand.website) && (
        <div className="max-w-2xl mx-auto px-6 md:px-10 pt-6">
          <div className="surface p-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            {brand.whatsapp && <a data-testid="brand-whatsapp" href={`https://wa.me/${brand.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"><MessageCircle className="h-4 w-4" /> {brand.whatsapp}</a>}
            {brand.phone && <a href={`tel:${brand.phone}`} className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"><Phone className="h-4 w-4" /> {brand.phone}</a>}
            {brand.email && <a href={`mailto:${brand.email}`} className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"><Mail className="h-4 w-4" /> {brand.email}</a>}
            {brand.instagram && <a href={`https://instagram.com/${brand.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"><Instagram className="h-4 w-4" /> {brand.instagram}</a>}
            {brand.website && <a href={brand.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"><Globe className="h-4 w-4" /> Site</a>}
          </div>
        </div>
      )}
    </>
  );
}

export default function PublicBooking() {
  const nav = useNavigate();
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") === "mine" ? "mine" : "new"); // new | mine
  const prefix = slug ? `/public/${slug}` : "/public";
  const [brand, setBrand] = useState({});
  const [me, setMe] = useState(null);


  const [services, setServices] = useState([]);
  const [pros, setPros] = useState([]);
  const [myAppts, setMyAppts] = useState([]);

  const refreshMyAppts = () => clientApi.get("/client/appointments").then(({ data }) => setMyAppts(data)).catch(() => {});

  // ---- Full-edit dialog (endereço, km, notas) — só até 1h antes ----
  const [editAppt, setEditAppt] = useState(null);
  const [editForm, setEditForm] = useState({ km: "", client_address: "", notes: "", services: [{ service_id: "", quantity: 1 }] });
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (a) => {
    setEditAppt(a);
    // Lê serviços existentes do agendamento. Agendamentos antigos salvos
    // antes da feature multi-serviço só tem `service_id` + `service_name`,
    // então fazemos fallback pra uma linha única.
    const existingServices = Array.isArray(a.services) && a.services.length
      ? a.services.map((s) => ({ service_id: s.service_id, quantity: s.quantity || 1 }))
      : [{ service_id: a.service_id || "", quantity: 1 }];
    setEditForm({
      km: a.km != null ? String(a.km) : "",
      client_address: a.client_address || "",
      notes: a.notes || "",
      services: existingServices,
    });
  };
  const closeEdit = () => setEditAppt(null);
  const canEdit = (a) => {
    try {
      const start = new Date(a.scheduled_at).getTime();
      return (start - Date.now()) >= 60 * 60 * 1000;
    } catch { return false; }
  };
  const saveEdit = async () => {
    if (!editAppt) return;
    // Só envia os campos que mudaram — evita sobrescrever com valor vazio
    // quando o usuário só ajustou o km.
    const payload = {};
    const origKm = editAppt.km != null ? String(editAppt.km) : "";
    if (editForm.km !== origKm) {
      const n = Number(editForm.km);
      if (!Number.isFinite(n) || n < 0) { toast.error("Km inválido"); return; }
      payload.km = n;
    }
    if ((editForm.client_address || "").trim() !== (editAppt.client_address || "").trim()) {
      if (!editForm.client_address.trim()) { toast.error("Endereço obrigatório"); return; }
      payload.client_address = editForm.client_address.trim();
    }
    if ((editForm.notes || "") !== (editAppt.notes || "")) {
      payload.notes = editForm.notes;
    }
    // Serviços — compara ids+qtd pra detectar diff. Só envia se mudou.
    const origServices = Array.isArray(editAppt.services) && editAppt.services.length
      ? editAppt.services.map((s) => ({ service_id: s.service_id, quantity: s.quantity || 1 }))
      : [{ service_id: editAppt.service_id || "", quantity: 1 }];
    const newServices = (editForm.services || []).filter((l) => l && l.service_id);
    const serializeSvcs = (arr) => JSON.stringify(arr.map((s) => [s.service_id, s.quantity]).sort());
    if (newServices.length > 0 && serializeSvcs(newServices) !== serializeSvcs(origServices)) {
      payload.services = newServices;
    }
    if (Object.keys(payload).length === 0) { closeEdit(); return; }
    setEditSaving(true);
    try {
      await clientApi.patch(`/client/appointments/${editAppt.id}`, payload);
      toast.success("Agendamento atualizado");
      closeEdit();
      refreshMyAppts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao atualizar"); }
    finally { setEditSaving(false); }
  };

  const editClientNotes = async (a) => {
    const next = window.prompt("Editar observação do agendamento:", a.notes || "");
    if (next === null) return;
    try {
      await clientApi.patch(`/client/appointments/${a.id}`, { notes: next });
      toast.success("Observação atualizada");
      refreshMyAppts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  // Reschedule dialog state — shows an availability calendar for the same
  // professional so the client can pick from real free slots.
  const [rsAppt, setRsAppt] = useState(null); // the appointment being rescheduled
  const [rsDay, setRsDay] = useState(new Date());
  const [rsSlots, setRsSlots] = useState([]);
  const [rsClosed, setRsClosed] = useState(false);
  const [rsLoading, setRsLoading] = useState(false);
  const [rsSubmitting, setRsSubmitting] = useState(false);
  const rsReqRef = useRef(0);

  const openReschedule = (a) => {
    setRsAppt(a);
    setRsDay(new Date(a.scheduled_at));
    setRsSlots([]);
    setRsClosed(false);
  };

  const closeReschedule = () => { setRsAppt(null); setRsSlots([]); };

  useEffect(() => {
    if (!rsAppt) return;
    const reqId = ++rsReqRef.current;
    setRsLoading(true);
    (async () => {
      try {
        // The reschedule endpoint uses the SAME professional and total
        // duration as the original appointment — we just need available
        // start-times for that combination on the picked day. Passamos o
        // endereço já registrado no agendamento para o backend aplicar o
        // gate de deslocamento (compara com vizinhos do mesmo dia).
        const params = {
          professional_id: rsAppt.professional_id,
          service_id: (rsAppt.services && rsAppt.services[0]?.service_id) || rsAppt.service_id,
          duration_minutes: rsAppt.duration_minutes,
          day: toISODateInput(rsDay),
        };
        if (rsAppt.client_address && String(rsAppt.client_address).trim().length > 5) {
          params.client_address = String(rsAppt.client_address).trim();
        }
        const { data } = await axios.get(`${API}${prefix}/availability`, { params });
        if (reqId !== rsReqRef.current) return;
        setRsSlots(data.slots || []);
        setRsClosed(!!data.closed);
      } catch {
        if (reqId !== rsReqRef.current) return;
        setRsSlots([]); setRsClosed(false);
      } finally {
        if (reqId === rsReqRef.current) setRsLoading(false);
      }
    })();
  }, [rsAppt, rsDay, prefix]);

  const rsShift = (n) => {
    const d = new Date(rsDay);
    d.setDate(d.getDate() + n);
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    if (d < startOfToday) return;
    setRsDay(d);
  };
  const rsIsToday = useMemo(() => {
    if (!rsDay) return false;
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const cur = new Date(rsDay); cur.setHours(0, 0, 0, 0);
    return d.getTime() === cur.getTime();
  }, [rsDay]);

  const confirmReschedule = async (slotIso) => {
    if (!rsAppt) return;
    setRsSubmitting(true);
    try {
      await clientApi.patch(`/client/appointments/${rsAppt.id}`, { scheduled_at: new Date(slotIso).toISOString() });
      toast.success("Agendamento remarcado");
      closeReschedule();
      refreshMyAppts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao remarcar"); }
    finally { setRsSubmitting(false); }
  };

  // Cancellation state — 1-hour rule enforced by both frontend hint and backend.
  const [cancelAppt, setCancelAppt] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const canCancel = (a) => {
    try {
      const start = new Date(a.scheduled_at).getTime();
      return (start - Date.now()) >= 60 * 60 * 1000; // >= 1 hour
    } catch { return false; }
  };
  const confirmCancel = async () => {
    if (!cancelAppt) return;
    setCanceling(true);
    try {
      await clientApi.patch(`/client/appointments/${cancelAppt.id}`, { status: "canceled" });
      toast.success("Agendamento cancelado");
      setCancelAppt(null);
      refreshMyAppts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao cancelar"); }
    finally { setCanceling(false); }
  };
  const [serviceLines, setServiceLines] = useState([{ service_id: "", quantity: 1 }]);
  const [proId, setProId] = useState("");
  const [day, setDay] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [dayClosed, setDayClosed] = useState(false);
  const [monthSummary, setMonthSummary] = useState({});
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthError, setMonthError] = useState(false);
  const [slot, setSlot] = useState(null);
  const [step, setStep] = useState(1);
  const [km, setKm] = useState("");
  const [address, setAddress] = useState("");
  const [showAllSlots, setShowAllSlots] = useState(false);
  const [notes, setNotes] = useState("");
  const [computingKm, setComputingKm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (slug) { try { localStorage.setItem("last_booking_slug", slug); } catch {} }
    const loginPath = slug ? `/agendar/login?slug=${slug}` : "/agendar/login";
    if (!getClientToken()) { nav(loginPath, { replace: true }); return; }
    (async () => {
      try {
        const [s, p, b, m] = await Promise.all([
          clientApi.get(`${prefix}/services`),
          axios.get(`${API}${prefix}/professionals`),
          axios.get(`${API}${prefix}/settings`),
          clientApi.get("/client/me"),
        ]);
        setServices(s.data); setPros(p.data); setBrand(b.data); setMe(m.data);
        applyBrandColor(b.data?.brand_color || "");
        applyTenantManifest(slug, b.data?.pwa_icon_url || "");
        setAddress(m.data.address || "");
        if (b.data.business_name) document.title = b.data.business_name + " — Agende online";
      } catch (e) { nav(loginPath, { replace: true }); }
    })();
  }, [nav, slug]);

  useEffect(() => {
    if (tab === "mine" && me) clientApi.get("/client/appointments").then(({ data }) => setMyAppts(data));
  }, [tab, me]);

  const validLines = useMemo(() => (serviceLines || []).filter((l) => l && l.service_id), [serviceLines]);
  const detailedLines = useMemo(() => validLines.map((l) => {
    const s = (services || []).find((x) => x && x.id === l.service_id);
    if (!s) return { ...l, service: null, subtotal: 0, minutes: 0 };
    const qty = Math.max(1, Number(l.quantity) || 1);
    const price = Number(s.price) || 0;
    const dur = Number(s.duration_minutes) || 0;
    return { ...l, quantity: qty, service: s, subtotal: price * qty, minutes: dur * qty };
  }), [validLines, services]);
  const totalMinutes = detailedLines.reduce((n, l) => n + l.minutes, 0);
  const totalServicesPrice = detailedLines.reduce((n, l) => n + l.subtotal, 0);
  const dispService = detailedLines.map((l) => l.service).find((s) => s?.has_displacement);
  const hasDisp = !!dispService;
  const total = useMemo(() => {
    const disp = hasDisp && km !== "" ? Number(km) * Number(dispService?.price_per_km || 0) : 0;
    return totalServicesPrice + disp;
  }, [totalServicesPrice, hasDisp, km, dispService]);

  const addServiceLine = () => setServiceLines((ls) => [...ls, { service_id: "", quantity: 1 }]);
  const removeServiceLine = (idx) => setServiceLines((ls) => ls.filter((_, i) => i !== idx));
  const updateServiceLine = (idx, patch) => setServiceLines((ls) => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const availabilityReqRef = useRef(0);
  useEffect(() => {
    if (!proId || detailedLines.length === 0 || totalMinutes <= 0) { setSlots([]); setDayClosed(false); return; }
    const reqId = ++availabilityReqRef.current;
    (async () => {
      try {
        const primaryServiceId = detailedLines[0].service_id;
        const params = { professional_id: proId, service_id: primaryServiceId, duration_minutes: totalMinutes, day: toISODateInput(day) };
        // Endereço do cliente é obrigatório: o backend usa para (a) calcular
        // tempo de deslocamento entre este slot e os agendamentos vizinhos do
        // dia (route-optimized availability) e (b) sinalizar recomendações.
        // Se o cliente ainda não digitou nada, o backend só retorna slots
        // livres pelo overlap padrão — travel-gate só liga com endereço.
        if (address && address.trim().length > 5) params.client_address = address.trim();
        const { data } = await axios.get(`${API}${prefix}/availability`, { params });
        // Discard stale responses when the user shifts the day / pro quickly.
        if (reqId !== availabilityReqRef.current) return;
        // We keep the "reason" (fixed_only_mismatch, closed) alongside the
        // slots array so the UI can show a specific message when closed.
        const arr = data.slots || [];
        arr.__reason = data.reason || null;
        setSlots(arr); setDayClosed(!!data.closed);
      } catch { /* ignore transient network errors */ }
    })();
  }, [proId, detailedLines, totalMinutes, day, prefix, address]);

  const monthReqRef = useRef(0);
  const primaryServiceId = detailedLines[0]?.service_id || "";
  useEffect(() => {
    if (!proId || !primaryServiceId || totalMinutes <= 0) { setMonthSummary({}); return; }
    const reqId = ++monthReqRef.current;
    const t = setTimeout(async () => {
      setMonthLoading(true);
      setMonthError(false);
      try {
        const params = { professional_id: proId, service_id: primaryServiceId, duration_minutes: totalMinutes, days: 30 };
        if (address && address.trim().length > 5) params.client_address = address.trim();
        const { data } = await axios.get(`${API}${prefix}/availability-month`, { params });
        if (reqId !== monthReqRef.current) return;
        const map = {};
        (data.days || []).forEach((d) => { map[d.day] = d; });
        setMonthSummary(map);
        const cur = map[toISODateInput(day)];
        if (!cur || cur.available === 0) {
          const first = (data.days || []).find((d) => d.available > 0);
          if (first) { setDay(new Date(`${first.day}T12:00:00`)); setSlot(null); }
        }
      } catch { if (reqId === monthReqRef.current) { setMonthSummary({}); setMonthError(true); } }
      finally { if (reqId === monthReqRef.current) setMonthLoading(false); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proId, primaryServiceId, totalMinutes, prefix, address]);

  const firstFreeDay = useMemo(() => Object.values(monthSummary).sort((a, b) => a.day.localeCompare(b.day)).find((d) => d.available > 0) || null, [monthSummary]);
  const monthDays = useMemo(() => Object.keys(monthSummary).sort(), [monthSummary]);
  const goNextAvailable = () => {
    if (!firstFreeDay?.first) return;
    setDay(new Date(`${firstFreeDay.day}T12:00:00`));
    setSlot(firstFreeDay.first);
    setStep(3);
  };

  const shift = (n) => {
    const d = new Date(day);
    d.setDate(d.getDate() + n);
    // Prevent navigating to a day before today (past days have no bookable
    // slots anyway; the arrow just does nothing when it would go backward).
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    if (d < startOfToday) return;
    setDay(d);
    setSlot(null);
  };
  const isToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const cur = new Date(day);
    cur.setHours(0, 0, 0, 0);
    return d.getTime() === cur.getTime();
  }, [day]);

  const computeKm = async (silent = false) => {
    if (!address) { if (!silent) toast.error("Informe seu endereço"); return; }
    setComputingKm(true);
    try {
      const r = await axios.post(`${API}${prefix}/geocode/distance`, { address });
      setKm(String(r.data.km));
      if (!silent) toast.success(`Distância ida+volta: ${r.data.km} km`);
    } catch (e) { if (!silent) toast.error("Cálculo indisponível — deixe em branco, confirmaremos depois"); }
    finally { setComputingKm(false); }
  };

  // Auto-recalculate the km whenever the address changes on the final step, so
  // the client no longer has to remember to press "Calcular distância" — the
  // total updates on its own and the route-optimized slots refresh too.
  const kmAutoRef = useRef("");
  useEffect(() => {
    if (!hasDisp) return;
    const addr = (address || "").trim();
    if (addr.length < 8 || addr === kmAutoRef.current) return;
    const t = setTimeout(() => { kmAutoRef.current = addr; computeKm(true); }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, hasDisp]);

  const submit = async () => {
    if (!slot) return;
    if (!(address || "").trim()) { toast.error("Informe o CEP/endereço para agendar"); setStep(3); return; }
    setSubmitting(true);
    try {
      const { data } = await clientApi.post("/client/booking", {
        professional_id: proId,
        services: detailedLines.map((l) => ({ service_id: l.service_id, quantity: l.quantity })),
        scheduled_at: new Date(slot).toISOString(),
        km: hasDisp && km !== "" ? Number(km) : null,
        client_address: address.trim(),
        notes,
      });
      setDone(data);
    } catch (e) { toast.error(errMsg(e, "Falha ao agendar")); }
    finally { setSubmitting(false); }
  };

  const logout = () => { setClientToken(null); localStorage.removeItem("client_user"); nav(slug ? `/agendar/login?slug=${slug}` : "/agendar/login", { replace: true }); };

  if (done) {
    return (
      <div className="min-h-screen bg-background">
        <BrandHeader brand={brand} />
        <div className="max-w-2xl mx-auto p-6 md:p-10">
          <div className="surface p-10 text-center space-y-4" data-testid="booking-success">
            <CheckCircle2 className="h-14 w-14 mx-auto text-primary" />
            <h1 className="text-2xl font-display font-bold">Agendamento confirmado!</h1>
            <p className="text-sm text-muted-foreground">Valor total: <span className="font-semibold text-foreground">{brl(done.price)}</span></p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => { setDone(null); setStep(1); setSlot(null); setKm(""); setNotes(""); setTab("mine"); setServiceLines([{ service_id: "", quantity: 1 }]); }} variant="outline">Ver meus agendamentos</Button>
              <Button onClick={() => { setDone(null); setStep(1); setSlot(null); setKm(""); setNotes(""); setServiceLines([{ service_id: "", quantity: 1 }]); }}>Novo agendamento</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader brand={brand} />
      <InstallBanner appName={brand?.business_name || "Agenda"} brandColor={brand?.brand_color} />
      <div className="max-w-2xl mx-auto p-6 md:p-10 space-y-6">
        {me && (
          <div className="flex items-center justify-between surface-muted p-3">
            <div className="text-sm">Olá, <span className="font-medium text-foreground">{me.name}</span></div>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="btn-cl-logout"><LogOut className="h-4 w-4 mr-1" /> Sair</Button>
          </div>
        )}

        <div className="flex gap-2">
          <Button variant={tab === "new" ? "default" : "outline"} onClick={() => setTab("new")} data-testid="tab-new-booking" className="flex-1 h-11"><CalendarCheck className="h-4 w-4 mr-2" /> Novo agendamento</Button>
          <Button variant={tab === "mine" ? "default" : "outline"} onClick={() => setTab("mine")} data-testid="tab-my-bookings" className="flex-1 h-11">Meus agendamentos</Button>
        </div>

        {tab === "mine" && (
          <div className="surface divide-y divide-border">
            {myAppts.length === 0 && <div className="p-12 text-center text-muted-foreground">Você ainda não tem agendamentos.</div>}
            {myAppts.map((a) => (
              <div key={a.id} data-testid={`my-appt-${a.id}`} className="p-4 flex flex-wrap gap-4 items-start">
                <div className="w-20 text-sm">
                  <div className="font-display font-semibold">{fmtTime(a.scheduled_at)}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(a.scheduled_at)}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{formatAppointmentServices(a)}</div>
                  <div className="text-xs text-muted-foreground">com {a.professional_name} · {a.duration_minutes} min</div>
                  {a.notes && <div className="text-xs mt-1 italic text-muted-foreground">"{a.notes}"</div>}
                  {a.status === "scheduled" && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <button
                        data-testid={`my-appt-edit-${a.id}`}
                        onClick={() => openEdit(a)}
                        disabled={!canEdit(a)}
                        title={canEdit(a) ? "Editar km, endereço e observação" : "Edição indisponível: faltam menos de 1 hora"}
                        className={`text-xs underline inline-flex items-center gap-1 ${canEdit(a) ? "text-primary" : "text-muted-foreground opacity-60 cursor-not-allowed"}`}
                      ><Pencil className="h-3 w-3" /> Editar</button>
                      <button data-testid={`my-appt-reschedule-${a.id}`} onClick={() => openReschedule(a)} className="text-xs underline text-primary inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Remarcar</button>
                      <button
                        data-testid={`my-appt-cancel-${a.id}`}
                        onClick={() => setCancelAppt(a)}
                        disabled={!canCancel(a)}
                        title={canCancel(a) ? "Cancelar agendamento" : "Cancelamento indisponível: faltam menos de 1 hora"}
                        className={`text-xs underline inline-flex items-center gap-1 ${canCancel(a) ? "text-destructive" : "text-muted-foreground opacity-60 cursor-not-allowed"}`}
                      ><XCircle className="h-3 w-3" /> Cancelar</button>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="font-semibold">{brl(a.price)}</div>
                  <span className={`text-xs px-2 py-1 rounded border ${a.status === "completed" ? "border-primary/30 text-primary bg-primary/5" : a.status === "canceled" ? "border-destructive/30 text-destructive bg-destructive/5" : "border-border text-muted-foreground"}`}>
                    {a.status === "scheduled" ? "Agendado" : a.status === "completed" ? "Concluído" : a.status === "canceled" ? "Cancelado" : "Faltou"}
                  </span>
                  {a.has_checklist && (
                    <div className="mt-2">
                      <a href={`/agendar/checklist/${a.id}?view=1`} className="text-xs underline text-primary" data-testid={`my-appt-checklist-${a.id}`}>Ver check-list</a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "new" && step === 1 && (
          <div className="surface p-6 space-y-4" data-testid="booking-step-1">
            <h2 className="font-display font-semibold text-lg">1. Escolha os serviços</h2>
            <div className="space-y-3">
              {serviceLines.map((line, idx) => {
                const s = services.find((x) => x.id === line.service_id);
                return (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`pb-service-line-${idx}`}>
                    <div className="col-span-8">
                      <Select value={line.service_id} onValueChange={(v) => updateServiceLine(idx, { service_id: v })}>
                        <SelectTrigger data-testid={`pb-select-service-${idx}`} className="h-12"><SelectValue placeholder="Selecione o serviço" /></SelectTrigger>
                        <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.duration_minutes}min · {brl(s.price)}</SelectItem>)}</SelectContent>
                      </Select>
                      {s && (
                        <div className="text-xs text-muted-foreground mt-1 pl-1">
                          {line.quantity}× {s.duration_minutes} min = <strong className="text-foreground">{line.quantity * s.duration_minutes} min</strong> · {brl(line.quantity * s.price)}
                        </div>
                      )}
                    </div>
                    <Input
                      data-testid={`pb-input-qty-${idx}`}
                      type="number"
                      min="1"
                      step="1"
                      value={line.quantity}
                      onChange={(e) => updateServiceLine(idx, { quantity: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                      className="col-span-3 h-12"
                      placeholder="Qtd"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="col-span-1 h-12 w-12"
                      data-testid={`pb-btn-remove-service-${idx}`}
                      onClick={() => removeServiceLine(idx)}
                      disabled={serviceLines.length <= 1}
                      aria-label="Remover"
                    >×</Button>
                  </div>
                );
              })}
              <Button type="button" variant="outline" size="sm" onClick={addServiceLine} data-testid="pb-btn-add-service">+ Adicionar outro serviço</Button>
            </div>
            {totalMinutes > 0 && (
              <div className="surface-muted p-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Tempo total reservado</span>
                <strong>{totalMinutes} min · {brl(totalServicesPrice)}</strong>
              </div>
            )}
            <div className="space-y-2">
              <Label>Profissional</Label>
              <Select value={proId} onValueChange={(v) => { setProId(v); setSlot(null); }}>
                <SelectTrigger data-testid="pb-select-pro" className="h-12"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{pros.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.role ? ` — ${p.role}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Inline day + slots — appears the moment the professional is
                chosen so the client sees ALL available times for that day
                without an extra "next" click. Clicking a slot auto-advances
                to step 3 (details). */}
            {proId && validLines.length > 0 && (
              <div className="space-y-3 pt-2 border-t border-border" data-testid="pb-inline-slots">
                {/* CEP + endereço são SEMPRE obrigatórios — usados para
                    calcular tempo de deslocamento entre este slot e os
                    agendamentos vizinhos do técnico no mesmo dia. Sem isso
                    a agenda pode liberar horário sem tempo de rota. */}
                <div className="rounded-lg border border-primary/30 p-3 space-y-2 bg-primary/5" data-testid="pb-address-required">
                  <Label className="text-sm inline-flex items-center gap-1">
                    <Route className="h-3.5 w-3.5" /> Seu endereço <span className="text-destructive">*</span>
                  </Label>
                  <CepField onFound={(line) => setAddress(line)} />
                  <Textarea rows={2} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Rua, número, bairro, cidade, UF" data-testid="pb-address-inline" />
                  <p className="text-xs text-muted-foreground">Precisamos do CEP e endereço para calcular o tempo de deslocamento até você.</p>
                  {hasDisp && km !== "" && (
                    <div className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1"><Route className="h-3 w-3" /> Distância ida+volta: <strong>{km} km</strong></div>
                  )}
                </div>
                {firstFreeDay ? (
                  <Button type="button" className="w-full h-auto min-h-11 py-2 whitespace-normal text-center leading-snug" onClick={goNextAvailable} data-testid="pb-next-available">
                    <CalendarCheck className="h-4 w-4 mr-2 shrink-0" />
                    <span>Próximo horário disponível: {WEEKDAY_PT[new Date(`${firstFreeDay.day}T12:00:00`).getDay()]} {firstFreeDay.day.slice(8, 10)}/{firstFreeDay.day.slice(5, 7)} às {firstFreeDay.first_label}</span>
                  </Button>
                ) : monthError ? (
                  <div className="text-sm text-center text-destructive rounded-lg border border-destructive/30 p-3" data-testid="pb-month-error">
                    Não foi possível carregar os dias livres agora. Tente novamente em instantes.
                  </div>
                ) : (!monthLoading && Object.keys(monthSummary).length > 0 && (
                  <div className="text-sm text-center text-muted-foreground rounded-lg border border-dashed border-border p-3" data-testid="pb-no-month-availability">
                    Nenhum horário livre nos próximos 30 dias com este profissional. Tente outro profissional.
                  </div>
                ))}
                <AvailabilityCalendar
                  summary={monthSummary}
                  loading={monthLoading}
                  selected={day}
                  onSelect={(d) => { setDay(d); setSlot(null); }}
                  from={monthDays.length ? new Date(`${monthDays[0]}T00:00:00`) : (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })()}
                  to={monthDays.length ? new Date(`${monthDays[monthDays.length - 1]}T23:59:59`) : (() => { const d = new Date(); d.setDate(d.getDate() + 29); return d; })()}
                />
                <div className="text-center" data-testid="pb-selected-day">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{WEEKDAY_PT[day.getDay()]}</div>
                  <div className="font-medium">{fmtDate(day)}</div>
                </div>
                {dayClosed ? (
                  <div className="text-center py-6 text-sm text-muted-foreground space-y-3" data-testid="pb-day-closed">
                    <div>
                      {(() => {
                        // Backend sends `reason:"fixed_only_mismatch"` quando o profissional
                        // é "ponto fixo" e o endereço do cliente não bate com o dele.
                        const reason = slots.__reason || null;
                        if (reason === "fixed_only_mismatch") {
                          return "Esse profissional só atende no ponto fixo dele. Escolha outro profissional ou informe o endereço correto do ponto fixo para agendar com ele.";
                        }
                        return "Profissional não atende neste dia.";
                      })()}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="pb-jump-next-day">
                      <ChevronRight className="h-4 w-4 mr-1" /> Ver próximo dia
                    </Button>
                  </div>
                ) : (() => {
                  // If the backend flagged this day as route-optimized (we
                  // sent a client_address AND the pro has other appts on the
                  // day), only surface the recommended slots to the client.
                  // The full list is still available under a "ver todos"
                  // toggle for edge cases (e.g. everyone else is far).
                  const anyRec = slots.some((s) => s.recommended && s.available);
                  const shownSlots = anyRec ? slots.filter((s) => s.recommended && s.available) : slots.filter((s) => s.available);
                  const anyAvailable = slots.some((s) => s.available);
                  const anyTravelBlocked = slots.some((s) => s.reason === "travel");
                  return (
                    <>
                      {anyRec && (
                        <div className="text-xs text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-500/30 rounded-lg px-3 py-2 mb-3" data-testid="pb-route-banner">
                          <Route className="h-3.5 w-3.5 inline mr-1" /> Mostrando apenas os horários que se encaixam na rota do técnico.
                        </div>
                      )}
                      {!anyAvailable && anyTravelBlocked && (
                        <div className="text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 border border-amber-500/30 rounded-lg px-3 py-2 mb-3">
                          <Route className="h-3.5 w-3.5 inline mr-1" /> Os horários deste dia não fecham com o tempo de deslocamento do técnico até o seu endereço. Vamos tentar o próximo dia.
                        </div>
                      )}
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {shownSlots.map((s) => (
                          <button
                            key={s.time}
                            data-testid={`pb-slot-${s.label}`}
                            disabled={!s.available}
                            onClick={() => { setSlot(s.time); setStep(3); }}
                            title={s.reason === "travel" ? "Sem tempo de deslocamento até seu endereço" : s.reason === "booked" ? "Já reservado" : s.reason === "blocked" ? "Horário bloqueado" : ""}
                            className={`h-12 rounded-lg border text-sm font-medium transition-colors relative
                              ${!s.available ? "border-border bg-muted/40 text-muted-foreground cursor-not-allowed" :
                                slot === s.time ? "border-primary bg-primary text-primary-foreground" :
                                s.recommended ? "border-emerald-500 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200" :
                                "border-border hover:border-primary hover:bg-primary/5"}`}
                          >
                            <Clock className="h-3 w-3 inline mr-1" /> {s.label}
                          </button>
                        ))}
                        {shownSlots.length === 0 && (
                          <div className="col-span-full text-sm text-muted-foreground py-4 text-center space-y-3">
                            <div>Sem horários disponíveis nesse dia.</div>
                            <Button variant="default" size="sm" onClick={() => shift(1)} data-testid="pb-jump-next-day">
                              <ChevronRight className="h-4 w-4 mr-1" /> Ver dia seguinte
                            </Button>
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            <Button data-testid="pb-next-1" disabled={validLines.length === 0 || !proId || !slot || !(address || "").trim()} className="w-full h-12" onClick={() => setStep(3)}>
              {!(address || "").trim() ? "Informe seu endereço" : slot ? "Continuar" : "Escolha um horário acima"}
            </Button>
          </div>
        )}

        {tab === "new" && step === 2 && (
          // Legacy standalone step 2 — no longer part of the main flow (slots
          // now render inline on step 1). Kept for backward compatibility with
          // deep links that may still point here; auto-forwards to step 1.
          <div className="surface p-6 space-y-4" data-testid="booking-step-2-legacy">
            <p className="text-sm text-muted-foreground">Redirecionando…</p>
            {(() => { setTimeout(() => setStep(1), 0); return null; })()}
          </div>
        )}

        {tab === "new" && step === 3 && (
          <div className="surface p-6 space-y-4" data-testid="booking-step-3">
            <h2 className="font-display font-semibold text-lg">3. Detalhes finais</h2>
            {slot && (
              <div className="surface-muted p-3 text-sm flex items-center gap-2" data-testid="pb-step3-summary">
                <CalendarCheck className="h-4 w-4 text-primary" />
                <span>{fmtDateTime(slot)}{pros.find((p) => p.id === proId) ? ` com ${pros.find((p) => p.id === proId).name}` : ""}</span>
              </div>
            )}
            {hasDisp && (
              <div className="surface-muted p-4 space-y-3">
                <div className="text-sm font-medium flex items-center gap-2"><Route className="h-4 w-4" /> Este serviço inclui deslocamento — {brl(dispService.price_per_km)}/km (ida+volta)</div>
                <div className="space-y-2">
                  <Label>Seu endereço</Label>
                  <CepField onFound={(line) => setAddress(line)} />
                  <Textarea data-testid="pb-address" rows={2} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Rua, número, bairro, cidade, UF" />
                </div>
                {km !== "" ? (
                  <div className="rounded-lg border border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm flex items-center justify-between" data-testid="pb-km-computed">
                    <span><Route className="h-4 w-4 inline mr-1 text-emerald-700" /> Distância ida+volta: <strong>{km} km</strong></span>
                    <button type="button" onClick={() => computeKm(false)} disabled={computingKm} className="text-xs underline text-muted-foreground" data-testid="pb-recalc-km">
                      {computingKm ? "recalculando…" : "recalcular"}
                    </button>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    {computingKm ? <><Loader2 className="h-3 w-3 animate-spin" /> Calculando distância…</> : "Preencha o endereço acima para calcular a distância."}
                  </div>
                )}
                {km !== "" && <div className="text-xs text-muted-foreground">Estimativa: {km} km · Deslocamento {brl(Number(km) * Number(dispService.price_per_km || 0))}</div>}
              </div>
            )}
            <div className="space-y-2">
              <Label>Observações para o técnico</Label>
              <Textarea
                data-testid="pb-notes"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex.: placa do veículo, ponto de referência, complemento do endereço, o que precisa ser feito..."
              />
              <p className="text-xs text-muted-foreground">Essas informações ficam visíveis para o profissional que irá te atender.</p>
            </div>
            <div className="surface-muted p-4 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">Total</div>
              <div className="text-xl font-display font-bold">{brl(total)}</div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="pb-back-3" variant="outline" onClick={() => setStep(1)}>Voltar</Button>
              <Button data-testid="pb-submit" disabled={submitting} className="flex-1 h-12" onClick={submit}>
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Confirmar agendamento
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Reschedule dialog — availability calendar for the same professional. */}
      <Dialog open={!!rsAppt} onOpenChange={(o) => { if (!o) closeReschedule(); }}>
        <DialogContent className="max-w-lg" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-display inline-flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Remarcar agendamento</DialogTitle>
            <DialogDescription>
              {rsAppt && (<>Atual: <strong>{fmtDateTime(rsAppt.scheduled_at)}</strong> · {rsAppt.duration_minutes} min com {rsAppt.professional_name}</>)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => rsShift(-1)} data-testid="rs-day-prev" disabled={rsIsToday} title="Dia anterior"><ChevronLeft className="h-4 w-4" /></Button>
              <div className="flex-1 text-center">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{WEEKDAY_PT[rsDay.getDay()]}</div>
                <div className="font-medium">{fmtDate(rsDay)}</div>
              </div>
              <Button variant="outline" size="icon" onClick={() => rsShift(1)} data-testid="rs-day-next" title="Próximo dia"><ChevronRight className="h-4 w-4" /></Button>
            </div>
            {rsLoading ? (
              <div className="py-8 flex items-center justify-center text-sm text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando horários…</div>
            ) : rsClosed ? (
              <div className="text-center py-6 text-sm text-muted-foreground">Profissional não atende neste dia. Use as setas para escolher outra data.</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
                {rsSlots.map((s) => (
                  <button
                    key={s.time}
                    data-testid={`rs-slot-${s.label}`}
                    disabled={!s.available || rsSubmitting}
                    onClick={() => confirmReschedule(s.time)}
                    className={`h-11 rounded-lg border text-sm font-medium transition-colors ${!s.available ? "border-border bg-muted/40 text-muted-foreground cursor-not-allowed" : "border-border hover:border-primary hover:bg-primary/5"}`}
                  ><Clock className="h-3 w-3 inline mr-1" /> {s.label}</button>
                ))}
                {rsSlots.length === 0 && <div className="col-span-full text-sm text-muted-foreground py-6 text-center">Sem horários disponíveis nesse dia.</div>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeReschedule} data-testid="rs-cancel">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation — 1-hour rule enforced both here and in the backend. */}
      <AlertDialog open={!!cancelAppt} onOpenChange={(o) => { if (!o) setCancelAppt(null); }}>
        <AlertDialogContent data-testid="cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Cancelar agendamento?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelAppt && (<>Você está prestes a cancelar o agendamento de <strong>{fmtDateTime(cancelAppt.scheduled_at)}</strong>. Esta ação não pode ser desfeita e o horário voltará a ficar disponível.</>)}
              <br /><br />
              <span className="text-xs">Só é possível cancelar com mais de <strong>1 hora</strong> de antecedência. Após esse prazo, entre em contato pelo WhatsApp.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-dialog-back">Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} disabled={canceling} data-testid="cancel-dialog-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {canceling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Sim, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit dialog — km, endereço, notas. Só até 1h antes do agendamento. */}
      <Dialog open={!!editAppt} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent className="max-w-lg" data-testid="edit-appt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Editar agendamento</DialogTitle>
            <DialogDescription>
              {editAppt && (<>Ajuste km, endereço e observação do atendimento de <strong>{fmtDateTime(editAppt.scheduled_at)}</strong>. Você pode editar até 1 hora antes.</>)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Serviços — cliente pode trocar/adicionar/remover até 1h antes.
                Mesmo picker usado no fluxo de novo agendamento. */}
            <div className="space-y-2">
              <Label className="text-sm inline-flex items-center gap-1"><Scissors className="h-3.5 w-3.5" /> Serviços</Label>
              {(editForm.services || []).map((line, idx) => {
                const s = services.find((x) => x.id === line.service_id);
                const updateLine = (patch) => setEditForm((f) => {
                  const next = [...(f.services || [])];
                  next[idx] = { ...next[idx], ...patch };
                  return { ...f, services: next };
                });
                const removeLine = () => setEditForm((f) => {
                  const next = (f.services || []).filter((_, i) => i !== idx);
                  return { ...f, services: next.length ? next : [{ service_id: "", quantity: 1 }] };
                });
                return (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`edit-svc-line-${idx}`}>
                    <div className="col-span-8">
                      <Select value={line.service_id} onValueChange={(v) => updateLine({ service_id: v })}>
                        <SelectTrigger data-testid={`edit-svc-select-${idx}`} className="h-11"><SelectValue placeholder="Selecione o serviço" /></SelectTrigger>
                        <SelectContent>{services.map((sv) => <SelectItem key={sv.id} value={sv.id}>{sv.name} — {sv.duration_minutes}min · {brl(sv.price)}</SelectItem>)}</SelectContent>
                      </Select>
                      {s && (
                        <div className="text-[11px] text-muted-foreground mt-1 pl-1">
                          {line.quantity}× {s.duration_minutes} min = {line.quantity * s.duration_minutes} min · {brl(line.quantity * s.price)}
                        </div>
                      )}
                    </div>
                    <Input
                      data-testid={`edit-svc-qty-${idx}`}
                      type="number" min="1" step="1"
                      value={line.quantity}
                      onChange={(e) => updateLine({ quantity: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                      className="col-span-3 h-11"
                    />
                    {(editForm.services || []).length > 1 && (
                      <Button type="button" variant="ghost" size="icon" className="col-span-1 h-11" onClick={removeLine} data-testid={`edit-svc-remove-${idx}`}>
                        <XCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
              <Button
                type="button" variant="outline" size="sm" data-testid="edit-svc-add"
                onClick={() => setEditForm((f) => ({ ...f, services: [...(f.services || []), { service_id: "", quantity: 1 }] }))}
              >+ Adicionar outro serviço</Button>
              <p className="text-[11px] text-muted-foreground">
                Trocar serviço pode alterar a <strong>duração</strong> e o <strong>preço</strong>. Se o novo tempo conflitar com outro horário do técnico, o sistema avisa.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm inline-flex items-center gap-1"><Route className="h-3.5 w-3.5" /> Endereço</Label>
              <CepField onFound={(line) => setEditForm((f) => ({ ...f, client_address: line }))} />
              <Textarea
                data-testid="edit-appt-address"
                rows={2}
                value={editForm.client_address}
                onChange={(e) => setEditForm((f) => ({ ...f, client_address: e.target.value }))}
                placeholder="Rua, número, bairro, cidade, UF"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Km (deslocamento ida + volta)</Label>
              <Input
                data-testid="edit-appt-km"
                type="number" min="0" step="0.1"
                value={editForm.km}
                onChange={(e) => setEditForm((f) => ({ ...f, km: e.target.value }))}
                placeholder="Ex.: 12.5"
              />
              {editAppt?.price_per_km ? (
                <p className="text-[11px] text-muted-foreground">
                  Taxa: {brl(editAppt.price_per_km)}/km · Deslocamento estimado: <strong>{brl((Number(editForm.km) || 0) * (editAppt.price_per_km || 0))}</strong>
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground">Este serviço não cobra deslocamento configurado.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Observação</Label>
              <Textarea
                data-testid="edit-appt-notes"
                rows={3}
                value={editForm.notes}
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Alguma informação extra pro técnico"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeEdit} data-testid="edit-appt-cancel">Cancelar</Button>
            <Button onClick={saveEdit} disabled={editSaving} data-testid="edit-appt-save">
              {editSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Salvar alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
